import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

@Directive({
    selector: '[appCurrencyInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CurrencyInputDirective),
            multi: true,
        },
    ],
})
export class CurrencyInputDirective implements ControlValueAccessor {
    private disabled = false
    private regex: RegExp = /^\d{0,9}(\.\d{0,2})?$/
    private specialKeys: string[] = ['Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Delete']
    private onChange: (value: number) => void = () => {}
    private onTouched: () => void = () => {}

    constructor(private el: ElementRef<HTMLInputElement>) {}

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (this.specialKeys.includes(event.key)) {
            return
        }
        if (this.disabled) {
            event.preventDefault()
            return
        }

        // Delay to wait for browser to update input
        setTimeout(() => {
            const rawValue = this.el.nativeElement.value.replace(/,/g, '')
            if (!this.regex.test(rawValue)) {
                this.el.nativeElement.value = rawValue.slice(0, -1) // Only remove invalid character, no formatting
            }
            // Do not format here
        })
    }

    @HostListener('input', ['$event.target.value'])
    onInput(value: string) {
        const rawValue = value.replace(/,/g, '')
        let numericValue = parseFloat(rawValue)

        // Always call onChange so Angular keeps the typed value
        if (!isNaN(numericValue)) {
            // floor
            numericValue = Math.floor(numericValue * 100) / 100
            this.onChange(numericValue)
        } else {
            this.onChange(null) // Pass null if user types ".", "1234.", etc.
        }
    }

    @HostListener('blur')
    onBlur() {
        const rawValue = this.el.nativeElement.value.replace(/,/g, '')
        if (!rawValue) {
            return
        }

        const number = parseFloat(rawValue)
        if (isNaN(number) || number < 0) {
            this.el.nativeElement.value = ''
            this.onChange(0)
            return
        }

        // If decimal part is .00 or .0 → show only integer part
        if (Number.isInteger(number) || rawValue.match(/\.0+$/)) {
            this.el.nativeElement.value = this.formatNumber(Math.floor(number).toString())
        } else {
            this.el.nativeElement.value = this.formatNumber(number.toFixed(2))
        }

        this.onTouched()
    }

    writeValue(value: number | null): void {
        if (value != null && !isNaN(value)) {
            if (value === 0) {
                this.el.nativeElement.value = '0' // Fix for 0 value (don't show "0.00")
            } else if (Number.isInteger(value)) {
                this.el.nativeElement.value = this.formatNumber(value.toString())
            } else {
                this.el.nativeElement.value = this.formatNumber(value.toFixed(2))
            }
        } else {
            this.el.nativeElement.value = ''
        }
    }

    registerOnChange(fn: (value: number) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled
        this.el.nativeElement.disabled = isDisabled
    }

    private formatNumber(value: string): string {
        const [intPart, decimalPart] = value.split('.')
        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        if (decimalPart !== undefined) {
            return `${formattedInt}.${decimalPart.padEnd(2, '0')}`
        }
        return formattedInt
    }
}
